import { MouseEvent } from 'react';
import { Tooltip } from 'react-tooltip';
import {
    DragDropContext,
    Draggable,
    Droppable,
    OnDragEndResponder,
} from '@hello-pangea/dnd';
import cn from 'classnames';

import { PacksDrawerRecord } from '../../types/packsDrawerTypes';

import styles from './PacksDrawerTable.module.scss';

type PacksDrawerTableProps = {
    records: PacksDrawerRecord[];
    getPackName: (packId: string) => string;
    onRecordClick: (id: PacksDrawerRecord['id']) => void;
    onDeleteRecordClick: (
        e: MouseEvent<HTMLButtonElement>,
        id: PacksDrawerRecord['id'],
    ) => void;
    onCopyRecordClick: (
        e: MouseEvent<HTMLButtonElement>,
        id: PacksDrawerRecord['id'],
    ) => void;
    onDragEnd: OnDragEndResponder;
    isDragDropDisabled: boolean;
};

export function PacksDrawerTable(props: PacksDrawerTableProps) {
    const {
        records,
        getPackName,
        onRecordClick,
        onDeleteRecordClick,
        onCopyRecordClick,
        onDragEnd,
        isDragDropDisabled,
    } = props;

    return (
        <table className={styles.packsDrawerTable}>
            <thead>
                <tr>
                    <th />
                    <th>Name</th>
                    <th>Pack ID</th>
                    <th>Active</th>
                    <th>Free</th>
                    <th className={styles.alignRight}>
                        <label htmlFor="buttons" />
                    </th>
                    <th className={styles.alignRight}>
                        <label htmlFor="draghandle" />
                    </th>
                </tr>
            </thead>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppablePacksDrawer">
                    {(provided) => (
                        <tbody
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {records.map((record, index) => (
                                <Draggable
                                    key={record.id.toString()}
                                    draggableId={record.id.toString()}
                                    index={index}
                                    isDragDisabled={isDragDropDisabled}
                                >
                                    {(provided, snapshot) => {
                                        const rowStyle = {
                                            ...provided.draggableProps.style,
                                            ...(snapshot.isDragging
                                                ? {
                                                      backgroundColor:
                                                          'var(--color-row-highlight, #EFF8FB)',
                                                  }
                                                : {}),
                                        };

                                        return (
                                            <tr
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                onClick={() =>
                                                    onRecordClick(record.id)
                                                }
                                                style={rowStyle}
                                            >
                                                <td>
                                                    {record.thumbnail ? (
                                                        <img
                                                            className={
                                                                styles.thumbnail
                                                            }
                                                            src={record.thumbnail.url.replace(
                                                                '{0}',
                                                                '@2x',
                                                            )}
                                                            alt={`${record.packId} thumbnail`}
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>
                                                    {getPackName(record.packId)}
                                                </td>
                                                <td>{record.packId}</td>
                                                <td>
                                                    {record.active
                                                        ? 'Active'
                                                        : 'Inactive'}
                                                </td>
                                                <td>
                                                    {record.isFree
                                                        ? 'Free'
                                                        : 'Paid'}
                                                </td>
                                                <td
                                                    className={cn(
                                                        styles.alignRight,
                                                        styles.actions,
                                                    )}
                                                >
                                                    <button
                                                        className="primary small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRecordClick(
                                                                record.id,
                                                            );
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="small"
                                                        onClick={(e) =>
                                                            onCopyRecordClick(
                                                                e,
                                                                record.id,
                                                            )
                                                        }
                                                    >
                                                        Copy to...
                                                    </button>
                                                    <button
                                                        className="danger small"
                                                        onClick={(e) =>
                                                            onDeleteRecordClick(
                                                                e,
                                                                record.id,
                                                            )
                                                        }
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                                <td
                                                    className={cn(
                                                        styles.alignRight,
                                                        'dragHandle',
                                                        isDragDropDisabled
                                                            ? styles.disabled
                                                            : '',
                                                    )}
                                                    {...provided.dragHandleProps}
                                                >
                                                    {isDragDropDisabled ? (
                                                        <div
                                                            data-tooltip-content="Clear search to enable reorder"
                                                            data-tooltip-id={
                                                                'drag-tooltip-' +
                                                                record.id
                                                            }
                                                        >
                                                            ≡
                                                        </div>
                                                    ) : (
                                                        <>≡</>
                                                    )}
                                                </td>
                                                {isDragDropDisabled && (
                                                    <Tooltip
                                                        id={
                                                            'drag-tooltip-' +
                                                            record.id
                                                        }
                                                        place="top-start"
                                                        delayShow={1000}
                                                    />
                                                )}
                                            </tr>
                                        );
                                    }}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </tbody>
                    )}
                </Droppable>
            </DragDropContext>
        </table>
    );
}
